import type { ChartConfig, ChartData } from '@indutrust/chart-model';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildStableGrid,
  buildTooltipStyle,
  hexToRgba,
  pickColor,
  resolvePalette,
} from './chart-option-chrome';
import { formatChartTooltipValue } from './format-chart-tooltip-value';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';
import type { SupportedChartConfiguration } from './supported-chart-configuration';

/**
 * Generate an ECharts configuration for an inline sparkline.
 *
 * Strips every piece of chrome — axes, grid lines, legend, series symbols —
 * so the series reads as a glyph beside a number rather than as a chart. The
 * value axis deliberately does **not** include zero: a national facility
 * count moving from 565 to 785 is a visible climb on its own scale but a
 * flat line when anchored to a zero baseline.
 *
 * Only the first dataset is plotted, and only its final point carries a
 * symbol, marking "where we are now" on the trajectory.
 *
 * Tooltip is appended to `document.body` (not confined to the canvas) because
 * sparklines sit in a 40px-tall, `overflow-hidden` KPI tile that would otherwise
 * crop the hover popup.
 *
 * @param data The input series and labels. Uses the first dataset. {@link ChartData}
 * @param config Chart configuration; only `colors` is honoured. {@link ChartConfig}
 * @param buildOptions Optional theme overrides.
 * @returns A {@link SupportedChartConfiguration} with `type: 'sparkline'`.
 * @example
 * const opt = createSparklineChartOptions(
 *   { labels: ['2023', '2024'], datasets: [{ name: 'Facilities', data: [70, 83] }] },
 *   { type: 'sparkline' }
 * );
 */
export function createSparklineChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const color = pickColor(resolvePalette(config, theme), 0);
  const hasDataset = data.datasets.length > 0;
  const [firstDataset] = data.datasets;
  const values = hasDataset ? firstDataset.data : [];
  const lastIndex = values.length - 1;

  return {
    type: 'sparkline',
    options: {
      animationDuration: 600,
      animationEasing: 'cubicOut',
      // 3px of breathing room stops the 2px stroke and the end symbol from
      // being clipped by the canvas edge. outerBoundsMode none keeps the
      // tiny box from fighting ECharts' default anti-overflow pass.
      grid: buildStableGrid({ top: 3, bottom: 3, left: 3, right: 3 }),
      tooltip: {
        ...buildTooltipStyle(theme),
        // Escape the 40px sparkline canvas and the KPI tile's overflow:hidden.
        appendTo: 'body',
        confine: false,
        trigger: 'axis',
        // The series name is the tile the sparkline sits in, so repeating it
        // in the tooltip is noise — show only the period and its value.
        formatter: (params: unknown): string => {
          const points: unknown[] = Array.isArray(params) ? params : [params];
          const [first] = points;
          if (typeof first !== 'object' || first === null) {
            return '';
          }
          const period = 'name' in first ? String(first.name) : '';
          const rawValue = 'value' in first ? first.value : '';
          const value =
            typeof rawValue === 'number' || typeof rawValue === 'string'
              ? formatChartTooltipValue(rawValue)
              : '';

          return `${period}: ${value}`;
        },
      },
      xAxis: {
        type: 'category',
        show: false,
        boundaryGap: false,
        data: data.labels,
      },
      yAxis: { type: 'value', show: false, scale: true },
      series: [
        {
          name: hasDataset ? firstDataset.name : 'Data',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          showSymbol: true,
          lineStyle: { color, width: 2 },
          itemStyle: { color },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: hexToRgba(color, 0.3) },
                { offset: 1, color: hexToRgba(color, 0) },
              ],
            },
          },
          data: values.map((value, index) => ({
            value,
            symbolSize: index === lastIndex ? 5 : 0,
          })),
        },
      ],
    },
  };
}
